// ViewOrder.js

const VIEW_ORDER_STEP = 100;

export function getViewIdentity(view) {
  return String(view?.key || view?.id || '').trim() || null;
}

export function normalizeViewPosition(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getViewLabel(view) {
  return String(view?.title || view?.label || '').trim();
}

export function compareViewsByConfiguredOrder(a, b) {
  const positionA = normalizeViewPosition(a?.position);
  const positionB = normalizeViewPosition(b?.position);

  if (positionA != null && positionB != null && positionA !== positionB) {
    return positionA - positionB;
  }

  if (positionA != null && positionB == null) return -1;
  if (positionA == null && positionB != null) return 1;

  const labelCompare = getViewLabel(a).localeCompare(getViewLabel(b), undefined, {
    sensitivity: 'base',
  });
  if (labelCompare !== 0) return labelCompare;

  return String(getViewIdentity(a) || '').localeCompare(String(getViewIdentity(b) || ''), undefined, {
    sensitivity: 'base',
  });
}

export function sortViewsByConfiguredOrder(views) {
  return [...(Array.isArray(views) ? views : [])].sort(compareViewsByConfiguredOrder);
}

export function moveOrderedViewById(views, fromId, toId) {
  const currentViews = Array.isArray(views) ? views : [];
  const normalizedFromId = String(fromId || '').trim();
  const normalizedToId = String(toId || '').trim();

  if (!normalizedFromId || !normalizedToId || normalizedFromId === normalizedToId) return null;

  const fromIndex = currentViews.findIndex((view) => getViewIdentity(view) === normalizedFromId);
  const toIndex = currentViews.findIndex((view) => getViewIdentity(view) === normalizedToId);

  if (fromIndex < 0 || toIndex < 0) return null;

  const nextViews = [...currentViews];
  const [movedView] = nextViews.splice(fromIndex, 1);
  nextViews.splice(toIndex, 0, movedView);
  return nextViews;
}

function buildComparableOrderedViews(views) {
  const orderedViews = sortViewsByConfiguredOrder(views);
  const explicitPositions = orderedViews
    .map((view) => normalizeViewPosition(view?.position))
    .filter((position) => position != null);

  let nextFallbackPosition = (explicitPositions.length
    ? Math.max(...explicitPositions)
    : 0) + VIEW_ORDER_STEP;
  let lastComparablePosition = Number.NEGATIVE_INFINITY;

  return orderedViews.map((view) => {
    const explicitPosition = normalizeViewPosition(view?.position);
    let comparablePosition = explicitPosition != null ? explicitPosition : nextFallbackPosition;

    if (comparablePosition <= lastComparablePosition) {
      comparablePosition = lastComparablePosition + VIEW_ORDER_STEP;
    }

    if (explicitPosition == null) {
      nextFallbackPosition = comparablePosition + VIEW_ORDER_STEP;
    }

    lastComparablePosition = comparablePosition;

    return {
      ...view,
      comparablePosition,
    };
  });
}

export function getNextViewPosition(views) {
  const comparableViews = buildComparableOrderedViews(views);
  const lastComparablePosition = comparableViews[comparableViews.length - 1]?.comparablePosition;

  if (lastComparablePosition == null) return VIEW_ORDER_STEP;
  return lastComparablePosition + VIEW_ORDER_STEP;
}

export function getMovedViewPosition(views, fromId, toId) {
  const orderedViews = buildComparableOrderedViews(views);
  const nextViews = moveOrderedViewById(orderedViews, fromId, toId);
  if (!nextViews) return null;

  const normalizedFromId = String(fromId || '').trim();
  const movedIndex = nextViews.findIndex((view) => getViewIdentity(view) === normalizedFromId);
  if (movedIndex < 0) return null;

  const previousComparablePosition = movedIndex > 0
    ? nextViews[movedIndex - 1]?.comparablePosition ?? null
    : null;
  const nextComparablePosition = movedIndex < nextViews.length - 1
    ? nextViews[movedIndex + 1]?.comparablePosition ?? null
    : null;

  if (previousComparablePosition == null && nextComparablePosition == null) {
    return VIEW_ORDER_STEP;
  }

  if (previousComparablePosition == null) {
    return nextComparablePosition - VIEW_ORDER_STEP;
  }

  if (nextComparablePosition == null) {
    return previousComparablePosition + VIEW_ORDER_STEP;
  }

  return previousComparablePosition + ((nextComparablePosition - previousComparablePosition) / 2);
}